import { TextField, TextFieldProps } from "@faro-lotv/flat-ui";
import type { DistanceUnitTypes } from "@faro-lotv/foundation";
import { debounce } from "es-toolkit";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import {
  convertDistanceToMeter,
  formatDistanceDecimal,
  getDistanceUnitSymbolText,
} from "../common/settings-context/distance-unit-utils";

type DistanceInputFieldProps = {
  /** Distance value to display in the input field */
  distanceInMeter: number | undefined;

  /** The distance unit to use for display and input */
  distanceUnit: DistanceUnitTypes;

  /** Callback when the distance value changes by the user input */
  onDistanceChanged(newDistanceInMeter: number | undefined): void;

  /**
   * The number of milliseconds to delay the distance changed callback.
   * Default is 300ms, it can be increased if the callback is expensive.
   */
  debounceMs?: number;
  /** Commit a draft before focus moves to a command such as Start Analysis. */
  flushOnBlur?: boolean;

  /**
   * Error message to display below the input field when user input is invalid.
   * If not provided, a default error message "Value must be a number" is used.
   */
  errorMessage?: string;

  /**
   * Client of `DistanceInputField` could perform custom validation on received
   * distance value, and provide a custom error message to display when the
   * validation fails.
   */
  customValidationError?: string;

  /** Optional label to display above the input field */
  label?: string;
} & Pick<
  TextFieldProps,
  | "sx"
  | "dark"
  | "fullWidth"
  | "disabled"
  | "variant"
  | "inputProps"
  | "autoFocus"
>;

/**
 * @returns a text field to input distance values
 */
export function DistanceInputField({
  distanceInMeter,
  onDistanceChanged,
  errorMessage,
  customValidationError,
  label,
  distanceUnit,
  debounceMs = 300,
  flushOnBlur = false,
  ...rest
}: DistanceInputFieldProps): JSX.Element {
  const labelWithUnit = useMemo(
    () =>
      label === undefined
        ? undefined
        : `${label}\u00A0(${getDistanceUnitSymbolText(distanceUnit)})`,
    [label, distanceUnit],
  );

  // Define the state variables for the text field
  const [errorText, setErrorText] = useState("");
  const [textField, setTextField] = useState(() =>
    distanceInMeter === undefined
      ? ""
      : formatDistanceDecimal(distanceInMeter, distanceUnit, false),
  );

  // Ref to keep track of the current distance value
  const distanceValueRef: MutableRefObject<number | undefined> =
    useRef(distanceInMeter);

  const distanceUnitRef = useRef(distanceUnit);

  // debounce delay to prevent update too often
  const [debounceChange] = useState(() =>
    debounce((value: number | undefined) => {
      onDistanceChanged(value);
    }, debounceMs),
  );

  // Update the UI when the value changes
  useEffect(() => {
    // Only update the text field if the distance value has changed externally
    if (
      distanceValueRef.current !== distanceInMeter ||
      distanceUnitRef.current !== distanceUnit
    ) {
      setTextField(
        distanceInMeter === undefined
          ? ""
          : formatDistanceDecimal(distanceInMeter, distanceUnit, false),
      );
      setErrorText("");
    }
    distanceValueRef.current = distanceInMeter;
    distanceUnitRef.current = distanceUnit;
  }, [distanceInMeter, distanceUnit]);

  const onTextChanged = useCallback(
    (newText: string) => {
      setTextField(newText);
      const newValue = Number(newText);

      if (!newText.trim() || isNaN(newValue)) {
        setErrorText(errorMessage ?? "Value must be a number");
        distanceValueRef.current = undefined;
        debounceChange(undefined);
      } else {
        setErrorText("");
        const newDistanceInMeter = convertDistanceToMeter(
          newValue,
          distanceUnit,
        );
        distanceValueRef.current = newDistanceInMeter;
        if (distanceInMeter !== newDistanceInMeter) {
          debounceChange(newDistanceInMeter);
        }
      }
    },
    [errorMessage, debounceChange, distanceUnit, distanceInMeter],
  );

  return (
    <TextField
      label={labelWithUnit}
      text={textField}
      error={customValidationError ?? errorText}
      onTextChanged={onTextChanged}
      onFocus={(ev) => {
        ev.target.select();
      }}
      onBlur={flushOnBlur ? () => debounceChange.flush() : undefined}
      {...rest}
    />
  );
}
